import { config } from "../config";

export interface ImageEnhancementRequest {
  image: string; // base64 encoded image
  styleId: string; // style ID instead of prompt
  model?: string;
}

export interface ImageEnhancementResponse {
  success: boolean;
  imageUrl?: string;
  error?: string;
}

/**
 * Service for calling the Azure Function to enhance images
 */
export class AzureFunctionService {
  private baseUrl: string;
  private endpoint: string;

  constructor() {
    this.baseUrl = config.azureFunction.baseUrl;
    this.endpoint = config.azureFunction.endpoint;

    // Ensure baseUrl has proper protocol
    if (
      !this.baseUrl.startsWith("http://") &&
      !this.baseUrl.startsWith("https://")
    ) {
      this.baseUrl = `https://${this.baseUrl}`;
    }

    // Remove trailing slash from baseUrl if present
    this.baseUrl = this.baseUrl.replace(/\/$/, "");

    // Log the final configuration for debugging
    console.log("AzureFunctionService initialized with:", {
      baseUrl: this.baseUrl,
      endpoint: this.endpoint,
      fullUrl: this.getEndpointUrl(),
    });
  }

  /**
   * Enhance an image using the Azure Function
   * @param image - Base64 encoded image
   * @param styleId - Style ID for enhancement
   * @param model - Optional model name (defaults to qwen-image-edit)
   * @returns Promise with the enhancement result
   */
  async enhanceImage(
    image: string,
    styleId: string,
    model: string = "qwen-image-edit"
  ): Promise<ImageEnhancementResponse> {
    try {
      const url = `${this.baseUrl}${this.endpoint}`;

      // Log the constructed URL for debugging
      console.log("Making request to:", url);
      console.log("Base URL:", this.baseUrl);
      console.log("Endpoint:", this.endpoint);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          image,
          styleId,
          model,
        } as ImageEnhancementRequest),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        console.error("Azure Function Error Response:", errorText);

        let errorMessage = "Unknown error";
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || "Unknown error";
        } catch (e) {
          errorMessage = errorText || "Unknown error";
        }

        throw new Error(
          `Azure Function request failed: ${response.status} - ${errorMessage}`
        );
      }

      const data: ImageEnhancementResponse = await response.json();
      console.log("Azure Function Response:", data);

      return data;
    } catch (error) {
      console.error("Error calling Azure Function:", error);
      throw error;
    }
  }

  /**
   * Get the full URL for the Azure Function endpoint
   * @returns The complete URL
   */
  getEndpointUrl(): string {
    return `${this.baseUrl}${this.endpoint}`;
  }

  /**
   * Validate that the constructed URL is properly formatted
   * @returns true if URL is valid, false otherwise
   */
  validateUrl(): boolean {
    try {
      const url = this.getEndpointUrl();
      new URL(url); // This will throw if URL is malformed
      return true;
    } catch (error) {
      console.error("Invalid URL constructed:", this.getEndpointUrl());
      return false;
    }
  }

  /**
   * Get debug information about the current configuration
   * @returns Object with configuration details
   */
  getDebugInfo() {
    return {
      baseUrl: this.baseUrl,
      endpoint: this.endpoint,
      fullUrl: this.getEndpointUrl(),
      isValidUrl: this.validateUrl(),
    };
  }
}

// Export a singleton instance
export const azureFunctionService = new AzureFunctionService();
